// inlined from https://github.com/ghengeveld/react-async, v6.2.0
/* eslint-disable */

/*
ISC License

Copyright (c) 2019 Gert Hengeveld

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 */

import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useReducer,
} from 'preact/hooks';

const statusTypes = {
	initial: 'initial',
	pending: 'pending',
	fulfilled: 'fulfilled',
	rejected: 'rejected',
};

const getInitialStatus = (value, promise) => {
	if (value instanceof Error) return statusTypes.rejected;
	if (value !== undefined) return statusTypes.fulfilled;
	if (promise) return statusTypes.pending;
	return statusTypes.initial;
};

const getIdleStatus = value => {
	if (value instanceof Error) return statusTypes.rejected;
	if (value !== undefined) return statusTypes.fulfilled;
	return statusTypes.initial;
};

const getStatusProps = status => ({
	status,
	isInitial: status === statusTypes.initial,
	isPending: status === statusTypes.pending,
	isLoading: status === statusTypes.pending, // alias
	isFulfilled: status === statusTypes.fulfilled,
	isResolved: status === statusTypes.fulfilled, // alias
	isRejected: status === statusTypes.rejected,
	isSettled:
		status === statusTypes.fulfilled || status === statusTypes.rejected,
});

const actionTypes = {
	start: 'start',
	cancel: 'cancel',
	fulfill: 'fulfill',
	reject: 'reject',
};

const init = ({ initialValue, promise, promiseFn }) => ({
	initialValue,
	data: initialValue instanceof Error ? undefined : initialValue,
	error: initialValue instanceof Error ? initialValue : undefined,
	value: initialValue,
	startedAt: promise || promiseFn ? new Date() : undefined,
	finishedAt: initialValue ? new Date() : undefined,
	...getStatusProps(getInitialStatus(initialValue, promise || promiseFn)),
	counter: 0,
});

const asyncReducer = (state, { type, payload, meta }) => {
	switch (type) {
		case actionTypes.start:
			return {
				...state,
				startedAt: new Date(),
				finishedAt: undefined,
				...getStatusProps(statusTypes.pending),
				counter: meta.counter,
			};
		case actionTypes.cancel:
			return {
				...state,
				startedAt: undefined,
				finishedAt: undefined,
				...getStatusProps(getIdleStatus(state.error || state.data)),
				counter: meta.counter,
			};
		case actionTypes.fulfill:
			return {
				...state,
				data: payload,
				value: payload,
				error: undefined,
				finishedAt: new Date(),
				...getStatusProps(statusTypes.fulfilled),
			};
		case actionTypes.reject:
			return {
				...state,
				error: payload,
				value: payload,
				finishedAt: new Date(),
				...getStatusProps(statusTypes.rejected),
			};
		default:
			return state;
	}
};

const dispatchMiddleware = dispatch => (action, ...args) => {
	dispatch(action, ...args);
	if (
		action.type === actionTypes.start &&
		typeof action.payload === 'function'
	) {
		action.payload();
	}
};

const noop = () => {};

export function useAsync(arg1, arg2) {
	const options =
		typeof arg1 === 'function' ? { ...arg2, promiseFn: arg1 } : arg1;

	const counter = useRef(0);
	const isMounted = useRef(true);
	const lastArgs = useRef(undefined);
	const prevOptions = useRef(undefined);
	const abortController = useRef({ abort: noop });

	const { reducer, dispatcher } = options;
	const _reducerState = useReducer(
		reducer
			? (state, action) => reducer(state, action, asyncReducer)
			: asyncReducer,
		options,
		init
	);
	const state = _reducerState[0];
	const _dispatch = _reducerState[1];

	const dispatch = dispatcher
		? action => dispatcher(action, dispatchMiddleware(_dispatch), options)
		: dispatchMiddleware(_dispatch);

	const getMeta = meta => ({ counter: counter.current, ...meta });

	const setData = (data, callback = noop) => {
		if (isMounted.current) {
			dispatch({
				type: actionTypes.fulfill,
				payload: data,
				meta: getMeta(),
			});
			callback();
		}
		return data;
	};

	const setError = (error, callback = noop) => {
		if (isMounted.current) {
			dispatch({
				type: actionTypes.reject,
				payload: error,
				error: true,
				meta: getMeta(),
			});
			callback();
		}
		return error;
	};

	const { onResolve, onReject } = options;
	const handleResolve = count => data =>
		count === counter.current &&
		setData(data, () => onResolve && onResolve(data));
	const handleReject = count => error =>
		count === counter.current &&
		setError(error, () => onReject && onReject(error));

	const start = promiseFn => {
		if ('AbortController' in window) {
			abortController.current.abort();
			abortController.current = new window.AbortController();
		}
		counter.current++;
		return new Promise((resolve, reject) => {
			if (!isMounted.current) return;
			const executor = () => promiseFn().then(resolve, reject);
			dispatch({
				type: actionTypes.start,
				payload: executor,
				meta: getMeta(),
			});
		});
	};

	const { promise, promiseFn, initialValue } = options;
	const load = () => {
		if (promise) {
			return start(() => promise).then(
				handleResolve(counter.current),
				handleReject(counter.current)
			);
		}
		const isPreInitialized = initialValue && counter.current === 0;
		if (promiseFn && !isPreInitialized) {
			return start(() =>
				promiseFn(options, abortController.current)
			).then(
				handleResolve(counter.current),
				handleReject(counter.current)
			);
		}
	};

	const { deferFn } = options;
	const run = (...args) => {
		if (deferFn) {
			lastArgs.current = args;
			return start(() =>
				deferFn(args, options, abortController.current)
			).then(
				handleResolve(counter.current),
				handleReject(counter.current)
			);
		}
	};

	const cancel = () => {
		counter.current++;
		abortController.current.abort();
		isMounted.current &&
			dispatch({ type: actionTypes.cancel, meta: getMeta() });
	};

	const { watch, watchFn } = options;
	useEffect(() => {
		if (
			watchFn &&
			prevOptions.current &&
			watchFn(options, prevOptions.current)
		) {
			load();
		}
	});
	useEffect(() => {
		promise || promiseFn ? load() : cancel();
	}, [promise, promiseFn, watch]);
	useEffect(() => () => (isMounted.current = false), []);
	useEffect(() => () => abortController.current.abort(), []);
	useEffect(() => (prevOptions.current = options) && undefined);

	return useMemo(
		() => ({
			...state,
			cancel,
			run,
			reload: () =>
				lastArgs.current ? run.apply(lastArgs.current) : load(),
			setData,
			setError,
		}),
		[state, deferFn, onResolve, onReject, dispatcher, reducer]
	);
}

const parseResponse = (accept, json) => res => {
	if (!res.ok) return Promise.reject(res);
	if (json === false) return res;
	if (json === true || accept === 'application/json') return res.json();
	return res;
};

export function useFetch(input, init, { defer, json, ...options } = {}) {
	const method = input.method || (init && init.method);
	const headers = input.headers || (init && init.headers) || {};

	const accept =
		headers['Accept'] ||
		headers['accept'] ||
		(headers.get && headers.get('accept'));

	const doFetch = (input, init) =>
		window.fetch(input, init).then(parseResponse(accept, json));

	const isDefer =
		defer === true || ~['POST', 'PUT', 'PATCH', 'DELETE'].indexOf(method);

	const fn = defer === false || !isDefer ? 'promiseFn' : 'deferFn';

	return useAsync({
		...options,
		[fn]: useCallback(
			(_, props, ctrl) =>
				doFetch(input, {
					signal: ctrl ? ctrl.signal : props.signal,
					...init,
				}),
			[JSON.stringify(input), JSON.stringify(init)]
		),
	});
}
